import React, {FC, useState} from 'react'
import {addDays, format} from 'date-fns'
import {
  publicApiKeysApi,
  PublicApiScope,
  PUBLIC_API_SCOPE_GROUPS,
} from '../services/publicApiKeysApi'

// Achado no code gate (2026-09-23): `new Date(Date.now() + 86400000).toISOString()` calcula
// "amanhã" em UTC, não no fuso local. Para quem está em UTC-3 (Brasília) perto da meia-noite,
// isso bloqueava a data de amanhã certa, um dia antes do que devia. addDays()/format() do
// date-fns operam no horário local do navegador, sem essa conversão.
const tomorrowLocalDate = () => format(addDays(new Date(), 1), 'yyyy-MM-dd')

interface CreateApiTokenModalProps {
  show: boolean
  onClose: () => void
  onCreated: (token: string, name: string) => void
}

const CreateApiTokenModal: FC<CreateApiTokenModalProps> = ({show, onClose, onCreated}) => {
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<PublicApiScope[]>([])
  const [expiresAt, setExpiresAt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setScopes([])
    setExpiresAt('')
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const toggleScope = (scope: PublicApiScope) => {
    setScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Dê um nome ao token, para reconhecê-lo depois (ex.: "Extensão Chrome").')
      return
    }
    if (scopes.length === 0) {
      setError('Selecione ao menos um escopo.')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const res = await publicApiKeysApi.create({
        name: name.trim(),
        scopes,
        expires_at: expiresAt ? `${expiresAt} 23:59:59` : null,
      })
      onCreated(res.api_token, name.trim())
      reset()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível criar o token.')
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Criar token da API pública</h5>
            <button type='button' className='btn-close' onClick={handleClose} aria-label='Fechar' />
          </div>
          <div className='modal-body'>
            {error && (
              <div className='alert alert-danger py-2' role='alert'>
                {error}
              </div>
            )}

            <div className='alert alert-info py-3 mb-4' role='alert'>
              O token completo só é mostrado uma vez, logo após criar. Se você perdê-lo, revogue
              este e crie outro.
            </div>

            <form id='create-api-token-form' onSubmit={handleSubmit}>
              <div className='mb-4'>
                <label className='form-label'>Nome</label>
                <input
                  type='text'
                  className='form-control'
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setError(null)
                  }}
                  placeholder='Ex.: Extensão Chrome, Bot Discord, n8n'
                  maxLength={100}
                  autoFocus
                />
              </div>

              <div className='mb-2'>
                <label className='form-label d-block'>Escopos</label>
                <div className='text-muted fs-8 mb-3'>
                  O que este token pode fazer. Escrita em clientes, times e usuários exige que o
                  usuário dono do token seja administrador ou gestor.
                </div>
                <div className='row g-3'>
                  {PUBLIC_API_SCOPE_GROUPS.map((group) => (
                    <div className='col-12 col-md-6' key={group.label}>
                      <div className='fw-bold fs-7 mb-2'>{group.label}</div>
                      {group.scopes.map((scope) => (
                        <div className='form-check mb-1' key={scope.value}>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id={`scope-${scope.value}`}
                            checked={scopes.includes(scope.value)}
                            onChange={() => toggleScope(scope.value)}
                          />
                          <label className='form-check-label fs-7' htmlFor={`scope-${scope.value}`}>
                            {scope.label}
                            {scope.comingSoon && (
                              <span className='badge badge-light-warning ms-2 fs-9'>em breve</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className='mb-2'>
                <label className='form-label'>Expira em (opcional)</label>
                <input
                  type='date'
                  className='form-control'
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  min={tomorrowLocalDate()}
                />
                <div className='text-muted fs-8 mt-1'>Deixe em branco para não expirar.</div>
              </div>
            </form>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={handleClose}>
              Cancelar
            </button>
            <button
              type='submit'
              form='create-api-token-form'
              className='btn btn-primary'
              disabled={loading}
            >
              {loading ? <span className='spinner-border spinner-border-sm me-2' /> : null}
              Criar token
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateApiTokenModal
